import { useEffect, useState } from 'react'
import { NavLink, useLocation } from 'react-router-dom'
import { Menu, X, Globe } from 'lucide-react'
import type { LucideIcon } from 'lucide-react'
import { UserMenu } from '@/components/shared/UserMenu'
import { webUrl } from '@/constants/site'
import { cn } from '@/lib/utils'

export type NavItem = {
  label: string
  icon:  LucideIcon
  to:    string
  badge?: number
  /** Passa `end` al NavLink (match esatto della rotta, usato dall'area utente) */
  end?:  boolean
}

export type NavSection = {
  label?: string
  items: NavItem[]
}

interface Props {
  sections: NavSection[]
}

/**
 * Topbar + drawer di navigazione per viewport < md (768px).
 * Da md in su non renderizza nulla di visibile (`md:hidden`): resta la
 * sidebar desktop dei layout. Stato locale, indipendente dallo zustand
 * `sidebarOpen` (che governa solo il collasso della sidebar desktop).
 */
export function MobileNav({ sections }: Props) {
  const [open, setOpen] = useState(false)
  const location = useLocation()

  // Chiudi il drawer a ogni cambio rotta
  useEffect(() => {
    setOpen(false)
  }, [location.pathname])

  // Esc per chiudere + scroll lock del body finché il drawer è aperto
  useEffect(() => {
    if (!open) return
    function onKey(e: KeyboardEvent) {
      if (e.key === 'Escape') setOpen(false)
    }
    document.addEventListener('keydown', onKey)
    const prevOverflow = document.body.style.overflow
    document.body.style.overflow = 'hidden'
    return () => {
      document.removeEventListener('keydown', onKey)
      document.body.style.overflow = prevOverflow
    }
  }, [open])

  const hasBadge = sections.some((s) => s.items.some((i) => (i.badge ?? 0) > 0))

  return (
    <>
      {/* ── Topbar ──────────────────────────────────────── */}
      <header className="sticky top-0 z-40 flex h-14 flex-shrink-0 items-center justify-between border-b border-gray-200 bg-white px-4 md:hidden">
        <button
          type="button"
          onClick={() => setOpen(true)}
          aria-label="Apri menu"
          aria-expanded={open}
          className="relative rounded-lg p-2 text-gray-600 hover:bg-gray-100"
        >
          <Menu className="h-5 w-5" />
          {hasBadge && (
            <span className="absolute right-1 top-1 h-2 w-2 rounded-full bg-holly-600" />
          )}
        </button>

        <a href={webUrl('/')} className="font-semibold text-holly-800 hover:text-holly-600">
          OLLYlife
        </a>

        <UserMenu compact placement="down" />
      </header>

      {/* ── Drawer ──────────────────────────────────────── */}
      {open && (
        <>
          <div
            className="fixed inset-0 z-40 bg-black/30 backdrop-blur-sm md:hidden"
            onClick={() => setOpen(false)}
            aria-hidden="true"
          />
          <aside className="fixed inset-y-0 left-0 z-50 flex w-72 max-w-[85vw] flex-col bg-white shadow-xl md:hidden">
            <div className="flex h-14 flex-shrink-0 items-center justify-between border-b px-4">
              <a href={webUrl('/')} className="font-semibold text-holly-800 hover:text-holly-600">
                OLLYlife
              </a>
              <button
                type="button"
                onClick={() => setOpen(false)}
                aria-label="Chiudi menu"
                className="rounded-lg p-1.5 text-gray-500 hover:bg-gray-100"
              >
                <X className="h-5 w-5" />
              </button>
            </div>

            <a
              href={webUrl('/')}
              title="Vai al sito pubblico"
              className="mx-2 mt-2 flex items-center gap-2 rounded-lg border border-gray-200 px-3 py-2 text-xs font-medium text-gray-600 hover:border-holly-300 hover:text-holly-700"
            >
              <Globe className="h-4 w-4 flex-shrink-0" />
              <span>Vai al sito</span>
            </a>

            <nav className="min-h-0 flex-1 space-y-1 overflow-y-auto p-2 py-3">
              {sections.map((section, si) => (
                <div key={section.label ?? `section-${si}`}>
                  {si > 0 && (
                    section.label ? (
                      <div className="px-3 pb-1 pt-3 text-[11px] font-semibold uppercase tracking-wide text-gray-400">
                        {section.label}
                      </div>
                    ) : (
                      <div className="mx-3 my-2 border-t border-gray-100" />
                    )
                  )}
                  <div className="space-y-0.5">
                    {section.items.map((item) => (
                      <NavLink
                        key={item.to}
                        to={item.to}
                        end={item.end}
                        className={({ isActive }) =>
                          cn(
                            'relative flex items-center gap-3 rounded-lg px-3 py-2.5 text-sm font-medium transition-colors',
                            isActive
                              ? 'bg-holly-50 text-holly-700'
                              : 'text-gray-600 hover:bg-gray-100 hover:text-gray-900'
                          )
                        }
                      >
                        <item.icon className="h-5 w-5 flex-shrink-0" />
                        <span>{item.label}</span>
                        {item.badge && item.badge > 0 ? (
                          <span className="ml-auto flex h-5 min-w-[20px] items-center justify-center rounded-full bg-holly-600 px-1.5 text-xs font-bold text-white">
                            {item.badge > 99 ? '99+' : item.badge}
                          </span>
                        ) : null}
                      </NavLink>
                    ))}
                  </div>
                </div>
              ))}
            </nav>
          </aside>
        </>
      )}
    </>
  )
}
